.#{$namespace}-tabs {
	--tabs-header-height: 40px;

	display: flex;
	flex-direction: column;

	&.is-left,
	&.is-right {
		flex-direction: row;

		.#{$namespace}-tabs__header {
			flex-direction: column;
		}

		.#{$namespace}-tabs__nav {
			flex-direction: column;
		}

		.#{$namespace}-tabs__item {
			border-bottom: none;
		}
	}

	&__header {
		display: flex;
		align-items: center;
		margin: 0 0 15px;
		border-bottom: 1px solid $border-light-color;
	}

	&.is-bottom &__header {
		margin: 15px 0 0;
		border-bottom: none;
		border-top: 1px solid $border-light-color;
	}

	&.is-left &__header {
		margin: 0 15px 0 0;
		border-bottom: none;
		border-right: 1px solid $border-light-color;
	}

	&.is-right &__header {
		margin: 0 0 0 15px;
		border-bottom: none;
		border-left: 1px solid $border-light-color;
	}

	&__nav {
		position: relative;
		display: flex;
		flex: 1;

		&.is-stretch {
			.#{$namespace}-tabs__item {
				flex: 1;
				text-align: center;
			}
		}
	}

	&__active-bar {
		// 拖着时 Vue 绑 var()，没写下之前先落到 0，避免非法值
		--sqt-tabs-bar-x: 0px;
		--sqt-tabs-bar-y: 0px;
		position: absolute;
		bottom: 0;
		left: 0;
		z-index: 1;
		height: 2px;
		pointer-events: none;
		background-color: #{color-token(primary)};

		&.is-ready {
			transition:
				width 0.3s cubic-bezier(0.645, 0.045, 0.355, 1),
				height 0.3s cubic-bezier(0.645, 0.045, 0.355, 1),
				transform 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
		}

		&.is-bottom {
			top: 0;
			bottom: auto;
		}

		&.is-left,
		&.is-right {
			top: 0;
			bottom: auto;
			width: 2px;
			height: auto;
		}

		&.is-left {
			right: 0;
			left: auto;
		}
	}

	&__item {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		gap: 6px;
		height: var(--tabs-header-height);
		padding: 0 20px;
		box-sizing: border-box;
		color: $text-secondary-color;
		font-size: $font-size;
		font-weight: 500;
		line-height: var(--tabs-header-height);
		white-space: nowrap;
		cursor: pointer;
		transition: color 0.2s;

		&.is-active {
			color: #{color-token(primary)};
		}

		&.is-disabled {
			color: $text-disabled-color;
			cursor: not-allowed;
		}

		&.is-sortable {
			// 没拖起来保持 pointer，一上来 grab 会像这项只能拖
			cursor: pointer;
			user-select: none;
			// 触屏不让页面跟着滚，否则阈值到了也拖不成
			touch-action: none;
		}

		&.is-drag-from {
			visibility: hidden;
		}
	}

	&__sort-ghost {
		position: fixed;
		z-index: 4000;
		box-sizing: border-box;
		pointer-events: none;
		margin: 0;
		// 挂 body，backdrop 才能糊到真实底下，不能写在带 transform 的原项上
		background-color: #{alpha-color($white-color, 40%)};
		backdrop-filter: blur(20px) saturate(140%);
		-webkit-backdrop-filter: blur(20px) saturate(140%);
		box-shadow: 0 8px 20px #{alpha-color($black-color, 16%)};
	}

	// 没在拖才换主色；is-sorting 时 :hover 会跟着指针扫过每一签
	&:not(.is-sorting) &__item:hover {
		color: #{color-token(primary)};
	}

	&.is-sorting {
		user-select: none;

		.#{$namespace}-tabs__item {
			cursor: grabbing;
		}

		.#{$namespace}-tabs__close:hover {
			// 划过关钮也不要出现圆底，否则像点了关闭
			background-color: transparent;
		}

		.#{$namespace}-tabs__active-bar.is-ready {
			// 跟手时不能走 0.3s，否则条永远慢一拍
			transition: none;
		}
	}

	&__close {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		font-size: 12px;
		border-radius: 50%;
		transition: background-color 0.2s;

		&:hover {
			background-color: #{alpha-color($black-color, 8%)};
		}
	}

	&__new {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 20px;
		height: 20px;
		margin: 0 10px;
		color: $text-secondary-color;
		cursor: pointer;
		border: 1px dashed $border-color;
		border-radius: 3px;

		&:hover {
			color: #{color-token(primary)};
			border-color: #{color-token(primary)};
		}
	}

	&__content {
		flex: 1;
		overflow: hidden;
		color: $text-primary-color;
	}

	&.is-card {
		.#{$namespace}-tabs__header {
			border-bottom: 1px solid $border-color;
		}

		.#{$namespace}-tabs__item {
			border: 1px solid transparent;
			border-bottom: none;
			border-radius: 4px 4px 0 0;
			background: #{alpha-color($black-color, 4%)};

			&.is-active {
				background: $white-color;
				border-color: $border-color;
				border-bottom-color: $white-color;
			}
		}
	}

	&.is-border-card {
		border: 1px solid $border-color;
		background: $white-color;
		box-shadow: 0 2px 4px #{alpha-color($black-color, 8%)};

		.#{$namespace}-tabs__header {
			margin: 0;
			background: #{alpha-color($black-color, 4%)};
			border-bottom: 1px solid $border-color;
		}

		.#{$namespace}-tabs__item {
			border: 1px solid transparent;
			margin-top: -1px;

			&.is-active {
				background: $white-color;
				border-color: $border-color;
				border-bottom-color: $white-color;
			}
		}

		.#{$namespace}-tabs__content {
			padding: 15px;
		}
	}
}
